// The losing tab, after someone else finalised this quote (BF5c-2f-iii).
//
// WHAT THIS IS FOR. Two tabs can hold the same quote. When one of them
// presses Save, the quote is finalised, and the backend then REFUSES the
// other tab's draft-ward autosave with 409 quote_finalised -- correctly, and
// permanently, because finalising is a one-way door. Before this banner the
// refusal was invisible: the losing tab looked like it was still working on a
// document it could no longer write to, and the person kept typing into it.
//
// ⚠️ THIS IS NOT A SAVE RESULT, which is why it is not SaveQuoteBanner. That
// component renders off `saveStatus` -- the outcome of a BUTTON PRESS -- so
// driving it from an autosave would print "Not saved." for a press nobody
// made, and the next press would clear a condition that has not gone away.
// This one describes the state of the QUOTE, so it stays up until the page
// leaves it.
//
// ⚠️ IT SAYS THE UNSAVED WORK IS UNSAVED. There is no reassuring wording
// available here that would also be true: edits made in this tab since the
// finalise are not in the database and this tab cannot put them there.
//
// ⚠️ THE ACTION IS "OPEN THE SAVED QUOTE", DELIBERATELY NOT "RELOAD".
// A reload would be actively harmful. Restore finds this tab's working state
// by asking for DRAFTS (findDraftFor -> listDrafts -> ?drafts=1), and the row
// it used to find is no longer a draft -- it is the finalised quote. So a
// reloaded tab finds nothing, starts a fresh draft from the same stale rows,
// and CREATES A SECOND QUOTE. Sending the user to the finalised record is
// what "refresh to latest" actually means here.

import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  semanticColors, radius, spacing, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export function QuoteFinalisedBanner({
  // The finalised quote's Odoo id. This is the draft row's id: finalising is
  // a transition on the SAME record, so the row this tab was autosaving into
  // is the quote that now exists. Optional only for safety -- a 409 comes
  // from the update path, which is only reached with an id -- and when it is
  // absent the banner still explains the state, just without the link.
  quoteId,
}: {
  quoteId?: number;
}) {
  return (
    <div
      data-testid="quote-finalised-banner"
      className="flex items-start"
      style={{
        gap: spacing[8],
        marginTop: spacing[12],
        padding: `${spacing[8]}px ${spacing[12]}px`,
        backgroundColor: semanticColors.warningBg,
        border: `1px solid ${semanticColors.warningBorder}`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.sm,
        color: semanticColors.warningTextOnTint,
      }}
    >
      <AlertTriangle
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        style={{ flexShrink: 0, marginTop: spacing[2], color: semanticColors.warning }}
      />
      <span>
        <strong>This quote was finalised elsewhere.</strong>{" "}
        Editing stopped, and any changes made here since then have not been saved.
        {quoteId !== undefined && (
          <>
            {" "}
            <Link
              to={`/saved-quotes/${quoteId}`}
              data-testid="quote-finalised-open-saved"
              style={{
                color: semanticColors.warningTextOnTint,
                fontWeight: typography.fontWeight.medium,
                textDecoration: "underline",
              }}
            >
              Open the saved quote
            </Link>
            {" to see what was saved."}
          </>
        )}
      </span>
    </div>
  );
}
